import React, { useState, useRef, useEffect } from 'react';
import { Calendar, MapPin } from 'lucide-react';
import LocationAutofill from './LocationAutofill';
import DateRangePicker from './DateRangePicker';

interface SearchFiltersProps {
  filters: {
    location: string;
    priceRange: string;
    bedrooms: string;
    dateFilter: {
      startDate: string;
      duration: number;
    };
  };
  onFiltersChange: (filters: any) => void;
  onSearch: (e: React.FormEvent | null) => void;
  onLocationSelect: (location: { name: string }) => void;
}

const SearchFilters: React.FC<SearchFiltersProps> = ({
  filters,
  onFiltersChange,
  onSearch,
  onLocationSelect
}) => {
  const [showDatePicker, setShowDatePicker] = useState(false);
  const datePickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target as Node)) {
        setShowDatePicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleDateSelect = (startDate: string, duration: number) => {
    onFiltersChange({
      ...filters,
      dateFilter: { startDate, duration }
    });
    setShowDatePicker(false);
  };

  const formatDateDisplay = () => {
    if (!filters.dateFilter.startDate) return 'Any dates';
    
    const date = new Date(filters.dateFilter.startDate);
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const formattedDate = `${monthNames[date.getMonth()]} ${date.getDate()}`;
    
    if (filters.dateFilter.duration === 0) return `From ${formattedDate}`;
    return `${formattedDate}, ${filters.dateFilter.duration} month${filters.dateFilter.duration > 1 ? 's' : ''}`;
  };

  return (
    <div className="sticky top-0 z-20 bg-white shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Location Search */}
          <div className="flex-1">
            <LocationAutofill
              value={filters.location}
              onChange={(value) => onFiltersChange({ ...filters, location: value })}
              onSelect={onLocationSelect}
              className="w-full"
            />
          </div>

          {/* Date Filter */}
          <div className="relative" ref={datePickerRef}>
            <button
              onClick={() => setShowDatePicker(!showDatePicker)}
              className="w-full md:w-auto px-4 py-2 bg-white border border-gray-300 rounded-lg flex items-center gap-2 hover:border-gray-400 transition-colors"
            >
              <Calendar className="h-5 w-5 text-gray-500" />
              <span className="text-gray-700">{formatDateDisplay()}</span>
            </button>

            {showDatePicker && (
              <div className="absolute right-0 mt-2 bg-white rounded-xl shadow-xl border border-gray-200 z-30">
                <DateRangePicker
                  startDate={filters.dateFilter.startDate}
                  duration={filters.dateFilter.duration}
                  onSelect={handleDateSelect}
                  onClose={() => setShowDatePicker(false)}
                />
              </div>
            )}
          </div>

          {/* Price Range */}
          <div>
            <select
              value={filters.priceRange}
              onChange={(e) => onFiltersChange({ ...filters, priceRange: e.target.value })}
              className="w-full md:w-auto px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">Any price</option>
              <option value="0-1000">Under $1,000</option>
              <option value="1000-2000">$1,000 - $2,000</option>
              <option value="2000-3000">$2,000 - $3,000</option>
              <option value="3000-4000">$3,000 - $4,000</option>
              <option value="4000+">$4,000+</option>
            </select>
          </div>

          {/* Bedrooms */}
          <div>
            <select
              value={filters.bedrooms}
              onChange={(e) => onFiltersChange({ ...filters, bedrooms: e.target.value })}
              className="w-full md:w-auto px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">Any beds</option>
              <option value="0">Studio</option>
              <option value="1">1 Bedroom</option>
              <option value="2">2 Bedrooms</option>
              <option value="3">3 Bedrooms</option>
              <option value="4">4+ Bedrooms</option>
            </select>
          </div>

          {/* Search Button */}
          <button
            onClick={(e) => onSearch(e)}
            className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Search
          </button>
        </div>
      </div>
    </div>
  );
};

export default SearchFilters;